import { useLayoutEffect, useRef, useState } from 'react'
import { LAYOUT } from './constants'
import { ContentMetadataText, hasMetadataMath } from '../components/common/ContentMetadataText'

interface TitleFit {
  scale: number
  height: number | undefined
}

export function TitleQueture({ grade, unitLabel, unitName, quetureTitle, bleedPx = 0 }: {
  grade: string
  unitLabel: string
  unitName: string
  quetureTitle: string
  bleedPx?: number
}) {
  const titleRef = useRef<HTMLParagraphElement>(null)
  const naturalRef = useRef<HTMLSpanElement>(null)
  const unitRowRef = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState<TitleFit>({ scale: 1, height: undefined })
  const hasMath = hasMetadataMath(quetureTitle)

  useLayoutEffect(() => {
    if (!hasMath) return
    let disposed = false
    const measure = () => {
      if (disposed) return
      const title = titleRef.current
      const natural = naturalRef.current
      if (!title || !natural || natural.offsetHeight === 0 || title.clientWidth === 0) return

      // Step 1 stays at its existing offset; fit only math titles into that header space.
      const availableHeight = LAYOUT.header.totalHeight + LAYOUT.step1FirstPaddingTop
        - LAYOUT.title.top - title.offsetTop - 8
      if (availableHeight <= 0) return
      const heightScale = availableHeight / natural.offsetHeight
      const widthScale = title.clientWidth > 0 && natural.scrollWidth > 0
        ? title.clientWidth / natural.scrollWidth
        : 1
      const scale = Math.min(1, heightScale, widthScale)
      const height = natural.offsetHeight * scale
      setFit(previous => previous.scale === scale && previous.height === height
        ? previous
        : { scale, height })
    }

    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    if (naturalRef.current) observer?.observe(naturalRef.current)
    if (unitRowRef.current) observer?.observe(unitRowRef.current)
    if (titleRef.current) observer?.observe(titleRef.current)
    const fonts = document.fonts
    void fonts?.ready?.then(measure, measure)
    fonts?.addEventListener('loadingdone', measure)

    return () => {
      disposed = true
      observer?.disconnect()
      fonts?.removeEventListener('loadingdone', measure)
    }
  }, [hasMath, quetureTitle, grade, unitLabel, unitName, bleedPx])

  return (
    <div className="absolute" style={{ top: LAYOUT.title.top + bleedPx, left: bleedPx, right: bleedPx, zIndex: 1 }}>
      {/* Grade + unit row - left-aligned at quetureMarginLeft */}
      <div ref={unitRowRef} className="flex gap-2 items-center" style={{ marginLeft: LAYOUT.title.quetureMarginLeft }}>
        <span
          className="shrink-0"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#E6E6E6',
            borderRadius: 100,
            padding: '4px 8px',
            fontSize: 16,
            lineHeight: 'normal',
            color: '#808080',
            fontWeight: 600,
            fontFamily: "'Pretendard', sans-serif",
          }}
        >
          {grade}
        </span>
        <span style={{ fontSize: 18, color: '#808080', fontWeight: 600, fontFamily: "'Pretendard', sans-serif" }}>
          {unitLabel}.
        </span>
        <span style={{ fontSize: 18, color: '#808080', fontWeight: 600, fontFamily: "'Pretendard', sans-serif" }}>
          <ContentMetadataText text={unitName} />
        </span>
      </div>
      {/* Queture title — '\n'을 명시적 줄바꿈으로 보존 (최대 2줄) */}
      <p
        ref={titleRef}
        className="font-dunggeunmiso"
        style={{
          fontSize: 38,
          color: '#1B2559',
          fontWeight: 700,
          lineHeight: 1.2,
          marginLeft: LAYOUT.title.quetureMarginLeft,
          marginTop: 16,
          letterSpacing: '-0.228px',
          whiteSpace: 'pre-line',
          maxWidth: 519,
          ...(hasMath ? { height: fit.height } : {}),
        }}
      >
        {hasMath ? (
          <span
            ref={naturalRef}
            style={{
              display: 'block',
              transform: fit.scale < 1 ? `scale(${fit.scale})` : undefined,
              transformOrigin: 'top left',
            }}
          >
            <ContentMetadataText text={quetureTitle} />
          </span>
        ) : <ContentMetadataText text={quetureTitle} />}
      </p>
    </div>
  )
}
